import type { Environment } from '../types/config';
import type { YardstickType } from '../types/metrics';

/**
 * Attempts to safely convert the error object in to a consistent, representable error object for
 * use with logging.
 * @arg error: The error object to convert.
 * @returns a record object filled out with the best information available or null if it was not
 * able to convert the object safely.
 */
export function safeGetError(error: unknown): Partial<Record<'error' | 'stack' | 'name', string>> | null {
  if (error instanceof Error) {
    return {
      error: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  if (typeof error === 'string') {
    return { error, name: 'string' };
  }

  if (typeof error === 'object' && error) {
    let message = '';
    let stack = '';

    if (typeof error.toString === 'function' && Object.prototype.toString !== error.toString) {
      message = error.toString();
    }

    if ('stack' in error) {
      stack = (error as { stack: string }).stack;
    }

    if (message || stack) {
      return {
        error: message,
        name: 'object',
        stack,
      };
    }

    try {
      return {
        error: JSON.stringify(error, null, 0),
        name: 'json',
      };
    } catch {
      return null;
    }
  }

  return null;
}

export function getYardstickEnv(env: Environment) {
  if (env === 'prod') {
    return 'prod';
  }

  return 'dev';
}

export function getYardstickPath(type: YardstickType): string {
  switch (type) {
    case 'counter':
      return 'increment';
    case 'timing':
      return 'timing';
    default:
      throw new Error(`Unrecognized YardstickMetricType: ${type}`);
  }
}
